/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1791331200000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Wed Oct 07 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "kommo",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to Kommo",
          "depth": 0,
          "id": "connect-to-kommo"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Contacts",
          "depth": 1,
          "id": "contacts"
        }, {
          "value": "Leads",
          "depth": 1,
          "id": "leads"
        }, {
          "value": "Companies",
          "depth": 1,
          "id": "companies"
        }, {
          "value": "Configure delete behavior",
          "depth": 1,
          "id": "configure-delete-behavior"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Behavior and limitations",
          "depth": 1,
          "id": "behavior-and-limitations"
        }, {
          "value": "Validate your setup",
          "depth": 1,
          "id": "validate-your-setup"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      a: "a",
      strong: "strong",
      ol: "ol",
      li: "li",
      ul: "ul",
      code: "code",
      h3: "h3",
      h4: "h4"
    }, _provideComponents(), props.components), {NoCommonErrors, LiveDebugger, SyncAlerts} = _components;
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "Kommo, formerly amoCRM, is a messenger-based sales CRM for managing leads and talking to customers over WhatsApp, Instagram, Telegram, and other channels. Sync contacts, leads, and companies from your warehouse to Kommo so your sales team works from warehouse data instead of manual entry."
      }), "\n", _jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Sync Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            }), _jsx(_components.th, {
              children: "API Reference"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "#contacts",
                children: _jsx(_components.strong, {
                  children: "Contacts"
                })
              })
            }), _jsx(_components.td, {
              children: "Create and update the people your team messages in Kommo."
            }), _jsx(_components.td, {
              children: "Upsert, Update, Insert"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://developers.kommo.com/reference/add-contacts",
                children: "Manage contacts ↗"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "#leads",
                children: _jsx(_components.strong, {
                  children: "Leads"
                })
              })
            }), _jsx(_components.td, {
              children: "Create and update deals, including their pipeline and stage."
            }), _jsx(_components.td, {
              children: "Upsert, Update, Insert"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://developers.kommo.com/reference/adding-leads",
                children: "Manage leads ↗"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "#companies",
                children: _jsx(_components.strong, {
                  children: "Companies"
                })
              })
            }), _jsx(_components.td, {
              children: "Create and update the organizations contacts belong to."
            }), _jsx(_components.td, {
              children: "Upsert, Update, Insert"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://developers.kommo.com/reference/add-companies",
                children: "Manage companies ↗"
              })
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about sync modes, refer to the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-kommo",
        children: "Connect to Kommo"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch authenticates to Kommo with a long-lived token from a ", _jsx(_components.a, {
          href: "https://developers.kommo.com/docs/private-integration",
          children: "private integration"
        }), ". You must have account administrator rights in Kommo to create one:"]
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["In Kommo, create a private integration. Leave ", _jsx(_components.strong, {
            children: "Redirect URL"
          }), " empty: Kommo requires it to be blank for long-lived tokens, and Hightouch doesn't use a redirect flow. You can leave ", _jsx(_components.strong, {
            children: "Access revoked notification web hook"
          }), " empty too."]
        }), "\n", _jsxs(_components.li, {
          children: ["Open the integration's ", _jsx(_components.strong, {
            children: "Keys and scopes"
          }), " tab and click ", _jsx(_components.strong, {
            children: "Generate long-lived token"
          }), "."]
        }), "\n", _jsx(_components.li, {
          children: "Select an expiration date and copy the token. Kommo doesn't show it again after you close the dialog."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Then enter the following in Hightouch:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Subdomain"
          }), ": the first part of your Kommo account URL. For example, enter ", _jsx(_components.code, {
            children: "mycompany"
          }), " if you sign in at ", _jsx(_components.code, {
            children: "mycompany.kommo.com"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Long-lived token"
          }), ": the token you generated."]
        }), "\n"]
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "Long-lived tokens expire on the date you selected, from one day to five years\nafter generation. When the token expires, syncs fail with an authentication\nerror until you generate a new token and update the destination. The token\ncarries the permissions of the administrator who generated it."
        })
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["Kommo syncs work the same way for contacts, leads, and companies. Use ", _jsx(_components.strong, {
          children: "Upsert"
        }), " when Hightouch should create missing records and update existing ones, ", _jsx(_components.strong, {
          children: "Update"
        }), " when it should change only records that already exist in Kommo, and ", _jsx(_components.strong, {
          children: "Insert"
        }), " when it should create new records and leave existing ones alone."]
      }), "\n", _jsx(_components.h3, {
        id: "contacts",
        children: "Contacts"
      }), "\n", _jsx(_components.h4, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsxs(_components.p, {
        children: ["Match contacts on ", _jsx(_components.strong, {
          children: "Phone"
        }), ", ", _jsx(_components.strong, {
          children: "Email"
        }), ", or ", _jsx(_components.strong, {
          children: "ID"
        }), ":"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Phone"
          }), " and ", _jsx(_components.strong, {
            children: "Email"
          }), " are predefined contact fields in every Kommo account, so you can match on them without creating anything in Kommo first. Hightouch matches on exact values, so normalize phone numbers in your model to the format stored in Kommo."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "ID"
          }), " is the Kommo contact ID. Use it when your warehouse already stores Kommo record IDs."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Changing the matching identifier on an existing Upsert sync can create duplicate contacts, because previously synced rows may no longer match their records."
      }), "\n", _jsxs(_components.p, {
        children: ["When you match on ", _jsx(_components.strong, {
          children: "Phone"
        }), " or ", _jsx(_components.strong, {
          children: "Email"
        }), ", set ", _jsx(_components.strong, {
          children: "Match field type"
        }), " to the type Kommo should file the value under, such as Mobile or Work. Kommo stores these fields as a list of typed values and falls back to Work when a sync doesn't name a type, which labels a mobile number as a work phone. Choose one type for the whole sync, or map a column when the type varies by row. Matching itself compares values and ignores the type."]
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "Map these standard contact fields: name, first name, last name, responsible user ID, and tags. The responsible user ID assigns the contact to a Kommo user and takes the user's numeric ID, not their name. Tags accept either a list or a comma-separated string. Kommo only adds tags, so dropping one from your model doesn't remove it from the record."
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch also loads your account's custom fields for each object, and you can map any of them. Values are typed to match the Kommo field type, such as text, numeric, monetary, checkbox, date, or date-time. Phone and email fields appear once per type, for example ", _jsx(_components.strong, {
          children: "Phone (Work)"
        }), " and ", _jsx(_components.strong, {
          children: "Phone (Mobile)"
        }), ", so pick the entry for the type you want the value filed under. Create custom fields in Kommo before you configure the sync so they appear in the mapping options."]
      }), "\n", _jsx(_components.h3, {
        id: "leads",
        children: "Leads"
      }), "\n", _jsx(_components.h4, {
        id: "record-matching-1",
        children: "Record matching"
      }), "\n", _jsxs(_components.p, {
        children: ["Leads match on ", _jsx(_components.strong, {
          children: "ID"
        }), ", the Kommo lead ID. To use Upsert or Update mode, your model must store the Kommo lead ID for existing leads. Without stored IDs, use Insert mode to create new leads."]
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping-1",
        children: "Field mapping"
      }), "\n", _jsxs(_components.p, {
        children: ["Map these standard lead fields: name, price, pipeline ID, status ID, responsible user ID, and tags. Pipeline ID and status ID place the lead in a pipeline and stage, and both take numeric Kommo IDs. You can find these IDs in your Kommo pipeline settings or through the Kommo API. Tags and custom fields work the same way as for ", _jsx(_components.a, {
          href: "#field-mapping",
          children: "contacts"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "companies",
        children: "Companies"
      }), "\n", _jsx(_components.h4, {
        id: "record-matching-2",
        children: "Record matching"
      }), "\n", _jsxs(_components.p, {
        children: ["Companies match on ", _jsx(_components.strong, {
          children: "ID"
        }), ", the Kommo company ID. As with leads, Upsert and Update modes require your model to store the Kommo ID."]
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping-2",
        children: "Field mapping"
      }), "\n", _jsxs(_components.p, {
        children: ["Map the standard company fields, name, responsible user ID, and tags, plus any of your account's custom fields. Tags behave the same way as for ", _jsx(_components.a, {
          href: "#field-mapping",
          children: "contacts"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "configure-delete-behavior",
        children: "Configure delete behavior"
      }), "\n", _jsxs(_components.p, {
        children: ["In Update mode, you choose what happens when a row leaves your model: clear the fields the sync writes to and leave the record in place, or select ", _jsx(_components.strong, {
          children: "Do nothing"
        }), " to leave the record untouched. Kommo's API doesn't support deleting contacts, leads, or companies, so syncs never delete Kommo records."]
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["For Hightouch platform error codes related to Kommo, see ", _jsx(_components.a, {
            href: "/developer-tools/error-codes#other-destinations",
            children: "Error codes: Other\ndestinations"
          }), "."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "behavior-and-limitations",
        children: "Behavior and limitations"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Kommo limits API traffic to seven requests per second per IP address, returns a ", _jsx(_components.code, {
            children: "429"
          }), " error when you exceed it, and blocks the IP after repeated violations. Hightouch stays under this limit by sending at most five requests per second with up to 50 records per request, so large syncs take longer to complete."]
        }), "\n", _jsx(_components.li, {
          children: "When Kommo rejects a request because some rows are invalid, Hightouch rejects only the rows Kommo names and continues syncing the rest. Rejected rows appear in the sync's row-level errors with the reason from Kommo."
        }), "\n", _jsxs(_components.li, {
          children: ["A ", _jsx(_components.code, {
            children: "401"
          }), " error means the long-lived token is invalid or expired. A ", _jsx(_components.code, {
            children: "403"
          }), " error means either that the integration lacks the rights for the object it's writing to, or that Kommo has blocked the IP address after sustained rate limit violations."]
        }), "\n", _jsxs(_components.li, {
          children: ["A ", _jsx(_components.code, {
            children: "422"
          }), " error means Kommo rejected the shape of the request itself. Hightouch fails these right away instead of retrying, because the same request fails the same way."]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "validate-your-setup",
        children: "Validate your setup"
      }), "\n", _jsx(_components.p, {
        children: "After your first sync completes, verify the data in Kommo:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Open the ", _jsx(_components.strong, {
            children: "Contacts"
          }), ", ", _jsx(_components.strong, {
            children: "Leads"
          }), ", or ", _jsx(_components.strong, {
            children: "Companies"
          }), " list and search for a synced record."]
        }), "\n", _jsx(_components.li, {
          children: "Confirm the mapped standard and custom fields show the expected values."
        }), "\n", _jsx(_components.li, {
          children: "Open a contact and confirm its phone or email sits under the type you chose, such as Mobile rather than Work."
        }), "\n", _jsx(_components.li, {
          children: "For leads, confirm the record landed in the expected pipeline and stage."
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(NoCommonErrors, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
